import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, RefreshCw, Download,
  IndianRupee, TrendingUp, Globe, MapPin,
  Baby, UserCheck, Percent
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt  = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;
const fmtK = (v) => {
  const n = Number(v || 0);
  if (n >= 10_000_000) return `₹${(n / 10_000_000).toFixed(1)}Cr`;
  if (n >= 100_000)    return `₹${(n / 100_000).toFixed(1)}L`;
  if (n >= 1_000)      return `₹${(n / 1_000).toFixed(0)}K`;
  return fmt(n);
};
const num  = (v) => Number(v || 0).toLocaleString("en-IN");

const AgentReport = () => {
  const { token } = useAuth();
  const now = new Date();
  const currentFY = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const [selectedYear, setSelectedYear] = useState(currentFY.toString());
  const [data, setData]     = useState([]);
  const [loading, setLoading] = useState(true);
  const years = Array.from({ length: 5 }, (_, i) => (currentFY - i).toString());
  const fyLabel = (y) => `FY ${y}-${String(Number(y) + 1).slice(2)}`;

  useEffect(() => { fetchData(); }, [selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/agent-wise?year=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data?.data || []);
    } catch {
      toast.error("Failed to load agent report");
    } finally {
      setLoading(false);
    }
  };

  // Totals
  const T = useMemo(() => data.reduce((acc, r) => ({
    agents:        acc.agents + 1,
    quotes:        acc.quotes        + (r.total_quotes         || 0),
    bookings:      acc.bookings      + (r.total_bookings        || 0),
    adults:        acc.adults        + (r.total_adults          || 0),
    children:      acc.children      + (r.total_children        || 0),
    infants:       acc.infants       + (r.total_infants         || 0),
    domestic:      acc.domestic      + (r.domestic_bookings     || 0),
    international: acc.international + (r.international_bookings|| 0),
    revenue:       acc.revenue       + (r.total_revenue         || 0),
    vendor:        acc.vendor        + (r.vendor_paid           || 0),
    gst:           acc.gst           + (r.gst_amount            || 0),
    profit:        acc.profit        + (r.profit                || 0),
  }), { agents:0, quotes:0, bookings:0, adults:0, children:0, infants:0, domestic:0, international:0, revenue:0, vendor:0, gst:0, profit:0 }), [data]);

  const convPct    = T.quotes > 0 ? ((T.bookings / T.quotes) * 100).toFixed(1) : "0.0";
  const marginPct  = T.revenue > 0 ? ((T.profit  / T.revenue) * 100).toFixed(1) : "0.0";

  // Summary stat tiles (14 items)
  const stats = [
    { label: "TOTAL ACTIVE AGENTS",           val: T.agents,                 icon: Users,       color: "text-violet-600", bg: "bg-violet-50",  border: "border-violet-100", fmt: (v) => v },
    { label: "TOTAL QUOTES",                  val: T.quotes,                 icon: TrendingUp,   color: "text-blue-600",   bg: "bg-blue-50",    border: "border-blue-100",   fmt: (v) => v },
    { label: "TOTAL BOOKINGS",                val: T.bookings,               icon: UserCheck,    color: "text-emerald-600",bg: "bg-emerald-50", border: "border-emerald-100",fmt: (v) => v },
    { label: "CONV %",                        val: convPct + "%",            icon: Percent,      color: "text-amber-600",  bg: "bg-amber-50",   border: "border-amber-100",  fmt: (v) => v },
    { label: "TOTAL ADULT",                   val: T.adults,                 icon: Users,        color: "text-indigo-600", bg: "bg-indigo-50",  border: "border-indigo-100", fmt: (v) => num(v) },
    { label: "TOTAL CHILD",                   val: T.children,               icon: Users,        color: "text-pink-600",   bg: "bg-pink-50",    border: "border-pink-100",   fmt: (v) => num(v) },
    { label: "TOTAL INFANT",                  val: T.infants,                icon: Baby,         color: "text-rose-500",   bg: "bg-rose-50",    border: "border-rose-100",   fmt: (v) => num(v) },
    { label: "TOTAL DOMESTIC BOOKINGS",       val: T.domestic,               icon: MapPin,       color: "text-teal-600",   bg: "bg-teal-50",    border: "border-teal-100",   fmt: (v) => v },
    { label: "TOTAL INTERNATIONAL BOOKINGS",  val: T.international,          icon: Globe,        color: "text-cyan-600",   bg: "bg-cyan-50",    border: "border-cyan-100",   fmt: (v) => v },
    { label: "TOTAL BOOKING AMT",             val: T.revenue,                icon: IndianRupee,  color: "text-slate-700",  bg: "bg-slate-50",   border: "border-slate-100",  fmt: fmtK },
    { label: "TOTAL VENDOR COST",             val: T.vendor,                 icon: IndianRupee,  color: "text-red-600",    bg: "bg-red-50",     border: "border-red-100",    fmt: fmtK },
    { label: "TOTAL GST",                     val: T.gst,                    icon: IndianRupee,  color: "text-orange-600", bg: "bg-orange-50",  border: "border-orange-100", fmt: fmtK },
    { label: "TOTAL PROFIT",                  val: T.profit,                 icon: IndianRupee,  color: "text-green-700",  bg: "bg-green-50",   border: "border-green-100",  fmt: fmtK },
    { label: "MARGIN %",                      val: marginPct + "%",          icon: Percent,      color: "text-violet-700", bg: "bg-violet-50",  border: "border-violet-100", fmt: (v) => v },
  ];

  const exportCSV = () => {
    if (!data.length) return;
    const headers = ["#","Agent","Quotes","Bookings","Conv%","Adult","Child","Infant","Domestic","International","Booking Amt","Vendor Cost","GST","Profit","Margin%"];
    const rows = data.map((r, i) => [
      i+1, r.agent_name, r.total_quotes, r.total_bookings,
      r.conversion_rate + "%",
      r.total_adults, r.total_children, r.total_infants,
      r.domestic_bookings, r.international_bookings,
      r.total_revenue, r.vendor_paid, r.gst_amount, r.profit,
      r.margin_pct + "%"
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `Agent_PL_Report_${fyLabel(selectedYear).replace(" ","_")}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  return (
    <DashboardLayout type="admin">
      <div className="space-y-5" data-testid="agent-report">

        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-6 h-6 text-violet-600" />
              Agent-wise P&amp;L Report
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">Detailed performance breakdown per agent — {fyLabel(selectedYear)}</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={selectedYear}
              onChange={e => setSelectedYear(e.target.value)}
              className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 cursor-pointer"
              data-testid="agent-fy-select"
            >
              {years.map(y => <option key={y} value={y}>{fyLabel(y)}</option>)}
            </select>
            <Button variant="outline" size="sm" onClick={fetchData} className="gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={exportCSV} disabled={!data.length} className="gap-1.5">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </Button>
          </div>
        </div>

        {/* ── Summary Stat Tiles (horizontal scroll on mobile) ── */}
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="agent-summary-tiles">
          <div className="flex gap-3 min-w-max sm:grid sm:grid-cols-7 sm:min-w-0 pb-1">
            {stats.map((s, i) => (
              <div
                key={i}
                className={`flex-shrink-0 w-36 sm:w-auto border ${s.border} ${s.bg} rounded-xl px-3 py-3 flex flex-col gap-1.5`}
                data-testid={`agent-stat-${i}`}
              >
                <div className="flex items-center gap-1.5">
                  <s.icon className={`w-3.5 h-3.5 ${s.color} shrink-0`} />
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide leading-tight">{s.label}</p>
                </div>
                <p className={`text-lg font-bold ${s.color} leading-none`}>
                  {loading ? "—" : s.fmt(s.val)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Data Table ── */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
              </div>
            ) : data.length === 0 ? (
              <div className="text-center py-16">
                <Users className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-400 font-medium">No data for {fyLabel(selectedYear)}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="agent-report-table">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/80">
                      <th className="px-3 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap sticky left-0 bg-gray-50/80">#</th>
                      <th className="px-3 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap sticky left-5 bg-gray-50/80 min-w-[150px]">Agent</th>
                      <th className="px-3 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Quotes</th>
                      <th className="px-3 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Bookings</th>
                      <th className="px-3 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Conv %</th>
                      <th className="px-3 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Adult</th>
                      <th className="px-3 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Child</th>
                      <th className="px-3 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Infant</th>
                      <th className="px-3 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Domestic</th>
                      <th className="px-3 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Intl.</th>
                      <th className="px-3 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Booking Amt</th>
                      <th className="px-3 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Vendor Cost</th>
                      <th className="px-3 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">GST</th>
                      <th className="px-3 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Profit</th>
                      <th className="px-3 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Margin %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {data.map((r, i) => {
                      const conv   = r.total_quotes > 0 ? ((r.total_bookings / r.total_quotes) * 100).toFixed(1) : "0.0";
                      const margin = r.margin_pct || 0;
                      return (
                        <tr key={r.agent_id || i} className="hover:bg-violet-50/30 transition-colors" data-testid={`agent-row-${i}`}>
                          <td className="px-3 py-3 text-xs text-gray-400 sticky left-0 bg-white">{i + 1}</td>
                          <td className="px-3 py-3 sticky left-5 bg-white">
                            <span className="font-semibold text-gray-800">{r.agent_name || "—"}</span>
                          </td>
                          <td className="px-3 py-3 text-right text-gray-600">{r.total_quotes}</td>
                          <td className="px-3 py-3 text-right text-gray-600">{r.total_bookings}</td>
                          <td className="px-3 py-3 text-right">
                            <span className={`inline-block text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                              conv >= 50 ? "bg-emerald-100 text-emerald-700"
                              : conv >= 25 ? "bg-amber-100 text-amber-700"
                              : "bg-gray-100 text-gray-500"
                            }`}>{conv}%</span>
                          </td>
                          <td className="px-3 py-3 text-right text-gray-600">{num(r.total_adults)}</td>
                          <td className="px-3 py-3 text-right text-gray-600">{num(r.total_children)}</td>
                          <td className="px-3 py-3 text-right text-gray-600">{num(r.total_infants)}</td>
                          <td className="px-3 py-3 text-right">
                            <span className="inline-block text-xs font-medium px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-700">{r.domestic_bookings || 0}</span>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <span className="inline-block text-xs font-medium px-1.5 py-0.5 rounded-full bg-cyan-50 text-cyan-700">{r.international_bookings || 0}</span>
                          </td>
                          <td className="px-3 py-3 text-right font-semibold text-gray-800 whitespace-nowrap">{fmt(r.total_revenue)}</td>
                          <td className="px-3 py-3 text-right text-rose-600 whitespace-nowrap">{fmt(r.vendor_paid)}</td>
                          <td className="px-3 py-3 text-right text-orange-600 whitespace-nowrap">{fmt(r.gst_amount || 0)}</td>
                          <td className="px-3 py-3 text-right font-semibold text-violet-700 whitespace-nowrap">{fmt(r.profit)}</td>
                          <td className="px-3 py-3 text-right">
                            <span className={`inline-block text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                              margin >= 20 ? "bg-emerald-100 text-emerald-700"
                              : margin >= 10 ? "bg-amber-100 text-amber-700"
                              : "bg-rose-100 text-rose-700"
                            }`}>{margin}%</span>
                          </td>
                        </tr>
                      );
                    })}

                    {/* ── Totals Row ── */}
                    <tr className="bg-violet-50/70 border-t-2 border-violet-200 font-semibold">
                      <td className="px-3 py-3 sticky left-0 bg-violet-50/70" />
                      <td className="px-3 py-3 sticky left-5 bg-violet-50/70">
                        <span className="text-xs font-bold text-violet-800 uppercase tracking-wide">TOTAL — {data.length} Agents</span>
                      </td>
                      <td className="px-3 py-3 text-right text-gray-700">{T.quotes}</td>
                      <td className="px-3 py-3 text-right text-gray-700">{T.bookings}</td>
                      <td className="px-3 py-3 text-right">
                        <span className="text-xs font-bold text-gray-700">{convPct}%</span>
                      </td>
                      <td className="px-3 py-3 text-right text-gray-700">{num(T.adults)}</td>
                      <td className="px-3 py-3 text-right text-gray-700">{num(T.children)}</td>
                      <td className="px-3 py-3 text-right text-gray-700">{num(T.infants)}</td>
                      <td className="px-3 py-3 text-right text-teal-700">{T.domestic}</td>
                      <td className="px-3 py-3 text-right text-cyan-700">{T.international}</td>
                      <td className="px-3 py-3 text-right text-gray-800 whitespace-nowrap">{fmt(T.revenue)}</td>
                      <td className="px-3 py-3 text-right text-rose-700 whitespace-nowrap">{fmt(T.vendor)}</td>
                      <td className="px-3 py-3 text-right text-orange-700 whitespace-nowrap">{fmt(T.gst)}</td>
                      <td className="px-3 py-3 text-right text-violet-800 whitespace-nowrap">{fmt(T.profit)}</td>
                      <td className="px-3 py-3 text-right">
                        <span className="text-xs font-bold text-violet-800">{marginPct}%</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AgentReport;
